<template>
  <!-- Modal backdrop -->
  <Transition name="modal-backdrop">
    <div
      v-if="modelValue"
      class="fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity duration-300"
      @click="handleBackdropClick"
    ></div>
  </Transition>

  <!-- Modal container -->
  <Transition name="modal">
    <div
      v-if="modelValue"
      class="fixed inset-0 z-50 overflow-y-auto flex items-center justify-center p-4"
      @click.self="handleBackdropClick"
    >
      <div :class="modalClasses" @click.stop>
        <!-- Modal Header -->
        <div v-if="hasHeader" class="flex items-center justify-between p-4 border-b border-gray-200">
          <slot name="header">
            <h3 class="text-lg font-semibold text-gray-900">{{ title }}</h3>
          </slot>
          <button
            v-if="closable"
            type="button"
            class="text-gray-400 hover:text-gray-600 transition-colors focus:outline-none"
            @click="handleClose"
          >
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <!-- Modal Body -->
        <div :class="bodyClasses">
          <slot></slot>
        </div>

        <!-- Modal Footer -->
        <div v-if="hasFooter" class="flex items-center justify-end gap-3 p-4 border-t border-gray-200 bg-gray-50">
          <slot name="footer">
            <BaseButton
              v-if="showCancelButton"
              variant="ghost"
              @click="handleCancel"
            >
              {{ cancelText }}
            </BaseButton>
            <BaseButton
              v-if="showConfirmButton"
              :variant="confirmVariant"
              :loading="confirmLoading"
              @click="handleConfirm"
            >
              {{ confirmText }}
            </BaseButton>
          </slot>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script>
import BaseButton from './BaseButton.vue'

export default {
  name: 'BaseModal',
  components: {
    BaseButton
  },
  props: {
    modelValue: {
      type: Boolean,
      default: false
    },
    title: {
      type: String,
      default: null
    },
    size: {
      type: String,
      default: 'md',
      validator: (value) => ['sm', 'md', 'lg', 'xl', 'full'].includes(value)
    },
    closable: {
      type: Boolean,
      default: true
    },
    closeOnBackdrop: {
      type: Boolean,
      default: true
    },
    showCancelButton: {
      type: Boolean,
      default: false
    },
    showConfirmButton: {
      type: Boolean,
      default: false
    },
    cancelText: {
      type: String,
      default: 'Annuler'
    },
    confirmText: {
      type: String,
      default: 'Confirmer'
    },
    confirmVariant: {
      type: String,
      default: 'primary'
    },
    confirmLoading: {
      type: Boolean,
      default: false
    },
    padding: {
      type: String,
      default: 'md',
      validator: (value) => ['none', 'sm', 'md', 'lg', 'xl'].includes(value)
    }
  },
  computed: {
    hasHeader() {
      return this.$slots.header || this.title
    },
    hasFooter() {
      return this.$slots.footer || this.showCancelButton || this.showConfirmButton
    },
    modalClasses() {
      return [
        'bg-white rounded-lg shadow-xl transform transition-all w-full',
        this.sizeClasses
      ]
    },
    sizeClasses() {
      const sizes = {
        sm: 'max-w-sm',
        md: 'max-w-md',
        lg: 'max-w-lg',
        xl: 'max-w-xl',
        '2xl': 'max-w-2xl',
        '3xl': 'max-w-3xl',
        '4xl': 'max-w-4xl',
        full: 'max-w-full mx-4'
      }
      return sizes[this.size]
    },
    bodyClasses() {
      const paddings = {
        none: 'p-0',
        sm: 'p-2',
        md: 'p-4',
        lg: 'p-6',
        xl: 'p-8'
      }
      return paddings[this.padding]
    }
  },
  methods: {
    handleClose() {
      this.$emit('update:modelValue', false)
      this.$emit('close')
    },
    handleBackdropClick() {
      if (this.closeOnBackdrop) {
        this.handleClose()
      }
    },
    handleCancel() {
      this.$emit('cancel')
      this.handleClose()
    },
    handleConfirm() {
      this.$emit('confirm')
    }
  },
  watch: {
    modelValue(newValue) {
      // Prevent body scroll when modal is open
      if (newValue) {
        document.body.style.overflow = 'hidden'
      } else {
        document.body.style.overflow = ''
      }
    }
  },
  beforeUnmount() {
    // Clean up body overflow when component is destroyed
    document.body.style.overflow = ''
  }
}
</script>

<style scoped>
/* Modal transitions */
.modal-backdrop-enter-active,
.modal-backdrop-leave-active {
  transition: opacity 0.3s ease;
}

.modal-backdrop-enter-from,
.modal-backdrop-leave-to {
  opacity: 0;
}

.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
  transform: scale(0.95) translateY(-20px);
}
</style>
